import React, { useCallback, useEffect, useState } from 'react';
import { CogIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';

interface AISettingsProps {
  onClose?: () => void;
  variant?: 'modal' | 'inline';
}

interface AiUsage {
  requestsToday: number;
  requestsDate: string;
  lastTokens: number | null;
  lastPromptTokens: number | null;
  lastCompletionTokens: number | null;
  lastTotalTokens: number | null;
  lastRequestAt: string | null;
}

export const AISettings: React.FC<AISettingsProps> = ({ onClose, variant = 'modal' }) => {
  const isInline = variant === 'inline';
  const [model, setModel] = useState('');
  const [modelDisplayName, setModelDisplayName] = useState('');
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [usage, setUsage] = useState<AiUsage | null>(null);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSaving, setIsSaving] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiService.getAiStatus();
      if (res.data) {
        setConfigured(!!res.data.configured);
        setModel(res.data.model || '');
        setModelDisplayName(res.data.modelDisplayName || res.data.model || '');
        setStatusMessage(res.data.message || '');
        setUsage(res.data.usage || null);
        setLatencyMs(res.data.latencyMs ?? null);
        if (res.data.ok) setConnectionStatus('success');
      } else {
        setConfigured(false);
        setStatusMessage(res.message || 'No se pudo cargar el estado');
      }
    } catch {
      setConfigured(false);
      setStatusMessage('Error de red al consultar el servidor');
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const testConnection = async () => {
    setIsTestingConnection(true);
    setConnectionStatus('idle');
    try {
      const res = await apiService.getAiStatus();
      if (res.data?.ok !== false && res.data?.configured) {
        setConnectionStatus('success');
        setStatusMessage(res.data.message || 'Conexión correcta');
        setConfigured(true);
        if (res.data.model) setModel(res.data.model);
        setModelDisplayName(res.data.modelDisplayName || res.data.model || '');
        setUsage(res.data.usage || null);
        setLatencyMs(res.data.latencyMs ?? null);
      } else {
        setConnectionStatus('error');
        setStatusMessage(res.data?.message || res.message || 'IA no configurada en el servidor');
        setConfigured(!!res.data?.configured);
      }
    } catch {
      setConnectionStatus('error');
      setStatusMessage('No se pudo contactar con el backend');
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!model.trim()) return;
    setIsSaving(true);
    try {
      const res = await apiService.saveAiConfig({ model: model.trim() });
      if (res.data) {
        setStatusMessage((res.data as { message?: string }).message || 'Modelo guardado');
        await loadStatus();
        onClose?.();
      } else {
        alert(res.message || res.error || 'No se pudo guardar la configuración');
      }
    } catch {
      alert('Error de red al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const tokensLast = usage?.lastTokens ?? usage?.lastTotalTokens;

  const content = (
  <>
    {!isInline && (
      <div className="flex items-center mb-4">
        <SparklesIcon className="h-6 w-6 text-violet-600 mr-2" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">
          Inteligencia artificial
        </h2>
      </div>
    )}

    {isInline && (
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Inteligencia artificial</h3>
        <p className="text-sm text-gray-600 dark:text-zinc-400 mt-1">
          Importación de documentos vía servidor (NVIDIA).
        </p>
      </div>
    )}

    <div className="space-y-4">
      <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50/80 dark:bg-zinc-800/40 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
              {modelDisplayName || 'Modelo IA'}
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              Proveedor NVIDIA · clave en servidor (<code className="text-[10px]">NVIDIA_API_KEY</code>)
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
              configured
                ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
                : 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${configured ? 'bg-emerald-500' : 'bg-amber-400'}`} />
            {configured === null ? '…' : configured ? 'Activa' : 'Sin clave'}
          </span>
        </div>
        {latencyMs != null && latencyMs > 0 && (
          <p className="text-xs text-zinc-500 mt-2">Última petición: {latencyMs} ms</p>
        )}
        {statusMessage ? <p className="mt-2 text-xs text-zinc-500">{statusMessage}</p> : null}
      </div>

      {usage && (
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 p-4 shadow-sm">
            <p className="text-xs text-zinc-500 uppercase tracking-wide">Peticiones hoy</p>
            <p className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mt-1 tabular-nums">
              {usage.requestsToday}
            </p>
          </div>
          <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 p-4 shadow-sm">
            <p className="text-xs text-zinc-500 uppercase tracking-wide">Últimos tokens</p>
            <p className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mt-1 tabular-nums">
              {tokensLast != null ? tokensLast : '—'}
            </p>
            {usage.lastPromptTokens != null && usage.lastCompletionTokens != null ? (
              <p className="text-[10px] text-zinc-400 mt-1 tabular-nums">
                {usage.lastPromptTokens} in · {usage.lastCompletionTokens} out
              </p>
            ) : null}
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
          ID del modelo
        </label>
        <input
          type="text"
          value={model}
          onChange={(e) => setModel(e.target.value)}
          placeholder="meta/llama-4-maverick-17b-128e-instruct"
          className="w-full px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-xl bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-violet-500"
        />
        <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">
          Por defecto: <code className="text-[10px]">meta/llama-4-maverick-17b-128e-instruct</code> (asistente y
          importación).{" "}
          <a
            href="https://build.nvidia.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-600 hover:underline"
          >
            build.nvidia.com
          </a>
        </p>
      </div>

      <div className="flex space-x-2 items-center">
        <button
          type="button"
          onClick={testConnection}
          disabled={isTestingConnection}
          className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 dark:text-zinc-200 bg-gray-100 dark:bg-zinc-800 border border-gray-300 dark:border-zinc-600 rounded-xl hover:bg-gray-200 dark:hover:bg-zinc-700 disabled:opacity-50"
        >
          {isTestingConnection ? (
            <span className="flex items-center justify-center">
              <CogIcon className="h-4 w-4 animate-spin mr-2" />
              Probando…
            </span>
          ) : (
            'Probar conexión'
          )}
        </button>
        {connectionStatus === 'success' && <span className="text-sm text-emerald-600">OK</span>}
        {connectionStatus === 'error' && <span className="text-sm text-amber-600">Sin conexión</span>}
      </div>
    </div>

    <div className={`flex justify-end space-x-3 ${isInline ? 'mt-4' : 'mt-6'}`}>
      {!isInline && onClose && (
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200"
        >
          Cancelar
        </button>
      )}
      <button
        type="button"
        onClick={handleSave}
        disabled={!model.trim() || isSaving}
        className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-xl hover:bg-violet-700 disabled:opacity-50"
      >
        {isSaving ? 'Guardando…' : 'Guardar modelo'}
      </button>
    </div>
  </>
  );

  if (isInline) {
    return (
      <div className="bg-white dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-700 rounded-2xl p-6 shadow-sm">
        {content}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
        {content}
      </div>
    </div>
  );
};
